/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import * as moment from 'moment';
import {firstValueFrom} from 'rxjs';
import {signal} from '@angular/core';
import {
  BEHAVIOUR_TIMES,
  BEHAVIOUR_TYPES,
  DEVICE_OS_TYPES,
  DEVICE_TYPES,
  FIELD_TYPE_OPERATIONS,
  VALID_DURATION_UNITS
} from './field-type-operations';
import {COUNTRIES} from '../common/constants/locations';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {
  networkDayFromPickerDate,
  pickerDateFromApiInstant
} from '../common/locale/network-date-picker-utils';
import {deferSignalStateWrite} from '../common/util/template-state';
import {SearchApiService} from '../common/controller/search-api.service';
import {FolderApiService} from '../folder/folder-api.service';
import {LocationApiService} from './location-api.service';
import {VisitorProfileFieldsApiService} from '../fields/visitor-profile-fields-api.service';

const logicInvalidWarnings = ['validation.warning.trigger.invalid.logic', 'validation.warning.trigger.unknown.variable'];

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').concat('AA,AB,AC,AD,AE,AF,AG,AH,AI,AJ,AK,AL,AM,AN,AO,AP,AQ,AR,AS,AT,AU,AV,AW,AX,AY,AZ'.split(','));

export const SPECIAL_BEHAVIOUR_DOMAIN_TYPES = [
  'DEVICE_OS', 'DEVICE_TYPE', 'REGION', 'COUNTRY', 'CITY', 'CLICKED_CATEGORY', 'CONVERTED_CATEGORY'
];

export const MATCHING_STYLES = ['ALL', 'ANY', 'CUSTOM'];

export interface TriggerFormLogicDeps {
  adnTimeShifter: any;
  folderApi: FolderApiService;
  searchApi: SearchApiService;
  visitorProfileFieldsApi: VisitorProfileFieldsApiService;
  locationApi: LocationApiService;
}

export class TriggerFormLogic {
  private readonly settingUpState = signal(false);
  private readonly allFoldersState = signal<any[]>([]);

  get settingUp(): boolean {
    return this.settingUpState();
  }

  set settingUp(value: boolean) {
    this.settingUpState.set(value);
  }

  get allFolders(): any[] {
    return this.allFoldersState();
  }

  set allFolders(value: any[]) {
    this.allFoldersState.set(value);
  }
  fields: any[] = [];
  nameToType: Record<string, string> = {};
  operationIdToControlType: Record<string, Record<string, string>> = {};
  nameToDescription: Record<string, string> = {};
  nameOpHelp: Record<string, Record<string, string>> = {};
  segmentResults: any[] = [];
  segmentResultsRemove: any[] = [];

  readonly behaviorTypes = BEHAVIOUR_TYPES;
  readonly fieldTypeOperations = FIELD_TYPE_OPERATIONS;
  readonly durationUnits = VALID_DURATION_UNITS;
  readonly pageViewTimes = BEHAVIOUR_TIMES;
  readonly deviceTypes = DEVICE_TYPES;
  readonly deviceOsTypes = DEVICE_OS_TYPES;
  readonly countries = COUNTRIES;
  readonly iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);

  constructor(
    readonly editingModel: any,
    private readonly deps: TriggerFormLogicDeps
  ) {
    this.init();
  }

  init(): void {
    const promise = firstValueFrom(this.deps.visitorProfileFieldsApi.get()).then((fields) => {
      this.fields = _.map(fields, (f) => ({
        name: f.name,
        dataType: f.type,
        displayName: _.lowerCase(f.name),
        description: f.description
      }));
      _.forEach(fields, (f) => {
        if (f.mapping) {
          this.fields.push({name: f.mapping, displayName: _.lowerCase(f.mapping), dataType: f.type});
        }
        _.forEach(f.aliases || [], (a) => {
          this.fields.push({name: a, displayName: _.lowerCase(a), dataType: f.type});
        });
      });

      this.nameToType = {};
      _.forEach(this.fields, (f) => {
        this.nameToType[f.name] = f.dataType;
      });

      this.operationIdToControlType = {};
      _.forEach(FIELD_TYPE_OPERATIONS, (val, key) => {
        this.operationIdToControlType[key] = {};
        _.forEach(val, (subVal) => {
          this.operationIdToControlType[key][subVal.id] = subVal.controlType;
        });
      });

      this.nameToDescription = {};
      _.forEach(this.fields, (f) => {
        this.nameToDescription[f.name] = f.description;
      });

      this.nameOpHelp = {};
      _.forEach(this.fields, (f) => {
        const el = FIELD_TYPE_OPERATIONS[f.dataType];
        this.nameOpHelp[f.name] = {};
        _.forEach(el, (val: any) => {
          if (val?.id) {
            this.nameOpHelp[f.name][val.id] = val.help;
          }
        });
      });
    });

    this.editingModel.folders = _.map(this.editingModel.folders, _.iteratee('id'));
    this.editingModel.matchingStyle = this.editingModel.matchingStyle || 'ALL';

    Promise.resolve(promise).then(() => {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.settingUp = true;
        this.afterInitAfterSubmit();
      });
    });

    this.deps.folderApi.listOptions().subscribe((page) => {
      deferSignalStateWrite(() => {
        this.allFolders = page.results;
      });
    });
  }

  afterInitAfterSubmit(): void {
    if (!this.settingUp) {
      return;
    }

    this.editingModel.global = this.editingModel.folders.length === 0;

    const allCriteria: any[] = [];
    if (!_.isEmpty(this.editingModel.conditions)) {
      _.forEach(this.editingModel.conditions, (v, k) => {
        if (v.period) {
          allCriteria.push({
            cType: 'BEHAVIOUR',
            type: v.type,
            time: v.period,
            condition: v.behaviour,
            conditionKey: k
          });
        } else {
          allCriteria.push({
            cType: 'CHARACTERISTIC',
            condition: v.condition,
            conditionKey: k
          });
        }
      });
    }

    this.editingModel.workingLogic = this.editingModel.logic;
    if (this.editingModel.logic) {
      const hasWarning = _.find(this.editingModel.validationWarnings, (vw) => logicInvalidWarnings.indexOf(vw.code) > -1);
      const hasAnd = this.editingModel.logic.indexOf('AND') > -1;
      const hasOr = this.editingModel.logic.indexOf('OR') > -1;
      const hasNot = this.editingModel.logic.indexOf('NOT') > -1;
      const hasBrackets = this.editingModel.logic.indexOf('(') > -1 || this.editingModel.logic.indexOf(')') > -1;

      if (hasWarning || hasNot || hasBrackets) {
        this.editingModel.matchingStyle = 'CUSTOM';
      } else if (!hasAnd && hasOr) {
        this.editingModel.matchingStyle = 'ANY';
      } else if (!hasOr) {
        this.editingModel.matchingStyle = 'ALL';
      } else {
        this.editingModel.matchingStyle = 'CUSTOM';
      }
    }

    this.editingModel.helpfulCriteria = _.map(allCriteria, (c) => this.parseCriterion(c));
    this.editingModel.helpfulCriteria = this.editingModel.helpfulCriteria || [];
    this.editingModel.helpfulCriteria = _.sortBy(this.editingModel.helpfulCriteria, (c) => _.get(c, 'conditionKey'));
  }

  private deriveFieldTypeOperation(dataType: string, opAsText: string, theSplit: string[]): any {
    return _.find(FIELD_TYPE_OPERATIONS[dataType], (entry) =>
      entry.operation === opAsText && (
        !entry.controlType ||
        (entry.controlType === 'MovingFeast' && theSplit.indexOf('ago') > -1) ||
        (entry.controlType !== 'MovingFeast' && theSplit.indexOf('ago') < 0)
      )
    );
  }

  private parseCriterion(c: any): any {
    const theSplit = c.condition.split(' ');
    let theField: string;
    let theOperationAsText: string;
    let theValue: any;
    let theOperationObj: any;

    if (c.cType === 'CHARACTERISTIC') {
      theField = theSplit[0].split(',')[0];
      theOperationAsText = theSplit[1];
      theValue = theSplit.slice(2).join(' ');

      const matchField = _.find(this.fields, (f) => f.name === theField);
      if (!matchField) {
        return {
          cType: 'CHARACTERISTIC',
          conditionKey: c.conditionKey,
          criterion: c,
          name: theField,
          value: theValue
        };
      }

      let durationUnit = 'days';
      if (matchField.dataType === 'Integer') {
        theValue = _.parseInt(theValue);
      }
      theOperationObj = this.deriveFieldTypeOperation(matchField.dataType, theOperationAsText, theSplit);
      if (!theOperationObj) {
        return {
          cType: 'CHARACTERISTIC',
          conditionKey: c.conditionKey,
          criterion: c,
          dataType: matchField.dataType,
          name: theField,
          value: theValue
        };
      }
      if (theOperationObj.controlType === 'MovingFeast') {
        _.forEach(VALID_DURATION_UNITS, (vdu) => {
          if (theValue.indexOf(' ' + vdu) > 0) {
            theValue = _.parseInt(theValue);
            durationUnit = vdu;
            return false;
          }
        });
      } else if (theOperationObj.controlType === 'Date') {
        theValue = pickerDateFromApiInstant(moment(theValue).toDate(), this.deps.adnTimeShifter);
      } else if (theOperationObj.controlType === 'DateAndTime') {
        // adn-date-time-picker expects a real UTC instant, not a browser-local container date.
        theValue = moment(theValue).toDate();
      }
      if (theOperationAsText.indexOf('in') > -1) {
        if (_.startsWith(theValue, '[')) {
          theValue = theValue.substr(1);
        }
        if (_.endsWith(theValue, ']')) {
          theValue = theValue.substr(0, theValue.length - 1);
        }
        theValue = _.split(theValue, ',').join(', ');
      }
      const dataObj: any = {
        cType: 'CHARACTERISTIC',
        conditionKey: c.conditionKey,
        criterion: c,
        dataType: matchField.dataType,
        name: theField,
        operation: theOperationObj,
        durationUnit
      };
      if (theOperationObj.controlType === 'Date' || theOperationObj.controlType === 'DateAndTime') {
        dataObj.valueDate = theValue;
      } else {
        dataObj.value = theValue;
      }
      return dataObj;
    }

    theField = theSplit
      .slice(0, theSplit.length - 2)
      .reduce((acc, cv) => acc + ' ' + cv)
      .split(',')[0];
    theOperationAsText = theSplit[theSplit.length - 2];
    theValue = theSplit[theSplit.length - 1];
    theOperationObj = this.deriveFieldTypeOperation('Behaviour', theOperationAsText, theSplit);
    return {
      cType: 'BEHAVIOUR',
      domain: theField,
      type: c.type,
      conditionKey: c.conditionKey,
      operation: theOperationObj,
      time: c.time,
      value: _.parseInt(theValue)
    };
  }

  getFieldOperations(fieldName: string): any[] {
    const dataType = this.nameToType[fieldName];
    if (!dataType || !this.fieldTypeOperations[dataType]) {
      return [];
    }
    return _.values(this.fieldTypeOperations[dataType]);
  }

  getBehaviourOperations(): any[] {
    return _.values(this.fieldTypeOperations.Behaviour);
  }

  getControlType(criterion: any): string | undefined {
    if (!criterion?.name || !criterion?.operation?.id) {
      return undefined;
    }
    const dataType = this.nameToType[criterion.name];
    return this.operationIdToControlType[dataType]?.[criterion.operation.id];
  }

  isTimestampOrDateField(criterion: any): boolean {
    const dataType = this.nameToType[criterion?.name];
    return dataType === 'Timestamp' || dataType === 'Date';
  }

  private getNextLetter(): string {
    let maxLetter = -1;
    _.forEach(this.editingModel.helpfulCriteria, (c) => {
      maxLetter = Math.max(alphabet.indexOf(c.conditionKey), maxLetter);
    });
    return alphabet[maxLetter + 1];
  }

  addCondition(): void {
    this.editingModel.helpfulCriteria.push({cType: 'CHARACTERISTIC', criterion: '', conditionKey: this.getNextLetter()});
  }

  addBehaviour(): void {
    this.editingModel.helpfulCriteria.push({cType: 'BEHAVIOUR', criterion: '', conditionKey: this.getNextLetter()});
  }

  removeCondition(index: number): void {
    _.pullAt(this.editingModel.helpfulCriteria, index);
  }

  segSearch = (searchElement: AdnSelectSearchElement, postFix?: string): void => {
    this.deps.searchApi.search({
      q: searchElement.search,
      types: 'UserSegment',
      objectState: 'ACTIVE',
      dataSource: 'ADNUNTIUS'
    }).subscribe((data) => {
      this['segmentResults' + (postFix || '')] = data.searchResults;
    });
  };

  searchRegion = (select: AdnSelectSearchElement, index: number): void => {
    this.deps.locationApi.search({q: select.search, types: 'REGION'}).subscribe((page) => {
      this.editingModel.helpfulCriteria[index].searchResults = select.search === '' ?
        [{name: this.editingModel.helpfulCriteria[index].domain}] :
        _.uniqBy(page.searchResults, 'name');
    });
  };

  searchCities = (select: AdnSelectSearchElement, index: number): void => {
    this.deps.locationApi.search({q: select.search, types: 'CITY'}).subscribe((page) => {
      this.editingModel.helpfulCriteria[index].searchResults = select.search === '' ?
        [{name: this.editingModel.helpfulCriteria[index].domain}] :
        _.uniqBy(page.searchResults, 'name');
    });
  };

  onBeforeSubmit(): void {
    const characteristics = _.map(_.filter(this.editingModel.helpfulCriteria, (c) =>
      c.cType === 'CHARACTERISTIC' && c.name && c.operation &&
      ((c.operation.controlType === 'Date' && moment(c.valueDate).isValid()) ||
        (c.operation.controlType === 'DateAndTime' && moment(c.valueDate).isValid()) ||
        _.isFinite(c.value) ||
        (_.isString(c.value) && _.trim(c.value).length > 0))
    ), (c) => {
      let theValue = c.value;
      if (c.operation.operation.indexOf('in') > -1) {
        theValue = '[' + _.map(c.value.split(','), (v) => _.trim(v)).join(',') + ']';
      }
      if (c.operation.controlType === 'MovingFeast') {
        theValue += ' ' + c.durationUnit + ' ago';
      } else if (c.operation.controlType === 'Date') {
        theValue = networkDayFromPickerDate(c.valueDate, this.deps.adnTimeShifter);
      } else if (c.operation.controlType === 'DateAndTime') {
        theValue = moment(c.valueDate).format();
      }
      return {
        key: c.conditionKey,
        value: c.name + ' ' + c.operation.operation + ' ' + theValue
      };
    });

    const behaviours: any[] = [];
    _.forEach(_.filter(this.editingModel.helpfulCriteria, (c) =>
      c.cType === 'BEHAVIOUR' && c.domain && c.operation && _.isFinite(c.value) && c.value >= 0 && c.time
    ), (c) => {
      behaviours.push({
        key: c.conditionKey,
        period: c.time,
        type: c.type,
        behaviour: c.domain + ' ' + c.operation.operation + ' ' + _.parseInt(c.value)
      });
    });

    this.editingModel.conditions = {};
    const conditionLetters: string[] = [];
    _.forEach(characteristics, (c) => {
      let letter = '';
      if (this.editingModel.matchingStyle === 'CUSTOM') {
        letter = c.key;
      } else {
        letter = alphabet[conditionLetters.length];
        conditionLetters.push(letter);
      }
      this.editingModel.conditions[letter] = {condition: c.value};
    });
    _.forEach(behaviours, (b) => {
      let letter = '';
      if (this.editingModel.matchingStyle === 'CUSTOM') {
        letter = b.key;
      } else {
        letter = alphabet[conditionLetters.length];
        conditionLetters.push(letter);
      }
      this.editingModel.conditions[letter] = b;
    });
    if (this.editingModel.matchingStyle === 'CUSTOM') {
      this.editingModel.logic = this.editingModel.workingLogic;
    } else {
      this.editingModel.logic = conditionLetters.join(this.editingModel.matchingStyle === 'ALL' ? ' AND ' : ' OR ');
    }
    this.editingModel.folders = this.editingModel.global ? [] : this.editingModel.folders;
  }

  onFolderChecklistChanged(): void {
    this.editingModel.global = false;
  }
}
